/**
 * URL scheme/origin checks for safe href and iframe src.
 * Use before rendering user- or API-controlled URLs to prevent javascript:/data: and open abuse.
 */

const ALLOWED_HTTP_SCHEMES = ["https:", "http:"] as const;
const HTTPS_ONLY = ["https:"] as const;

function getScheme(url: string): string | null {
	try {
		const parsed = new URL(url, "https://dummy.invalid");
		return parsed.protocol;
	} catch {
		return null;
	}
}

/**
 * Allow only http: or https: (no javascript:, data:, etc.).
 */
export function isSafeHttpOrHttpsUrl(url: string | null | undefined): boolean {
	if (url == null || typeof url !== "string" || !url.trim()) return false;
	const scheme = getScheme(url.trim());
	return scheme !== null && (ALLOWED_HTTP_SCHEMES as readonly string[]).includes(scheme);
}

/**
 * Allow only https: (e.g. for file/download URLs from API).
 */
export function isSafeHttpsOnlyUrl(url: string | null | undefined): boolean {
	if (url == null || typeof url !== "string" || !url.trim()) return false;
	const scheme = getScheme(url.trim());
	return scheme !== null && (HTTPS_ONLY as readonly string[]).includes(scheme);
}

const STRIPE_ORIGINS = [
	"https://billing.stripe.com",
	"https://checkout.stripe.com",
	"https://invoice.stripe.com",
	"https://js.stripe.com",
	"https://dashboard.stripe.com",
];

/**
 * Allow only Stripe billing/checkout URLs.
 */
export function isSafeStripeUrl(url: string | null | undefined): boolean {
	if (url == null || typeof url !== "string" || !url.trim()) return false;
	try {
		const parsed = new URL(url.trim());
		const origin = parsed.origin.toLowerCase();
		return STRIPE_ORIGINS.some((allowed) => origin === allowed);
	} catch {
		return false;
	}
}

/**
 * Safe for <Link href>: relative path (starts with /) or http(s) URL.
 */
export function isSafeLinkHref(url: string | null | undefined): boolean {
	if (url == null || typeof url !== "string" || !url.trim()) return false;
	const u = url.trim();
	if (u.startsWith("/") && !u.startsWith("//")) return true;
	return isSafeHttpOrHttpsUrl(u);
}

/** Allowed extra origins for iframe src (marketplace + primary site). */
const DEFAULT_IFRAME_ALLOWLIST_ORIGINS = [
	"https://techxeed.com",
	"https://www.techxeed.com",
].filter(Boolean);

/**
 * For iframe src: allow same-origin, localhost (any port), and optional allowlist.
 * Prevents embedding arbitrary external sites.
 */
export function isSafeIframeSrc(
	url: string | null | undefined,
	options?: { allowLocalhost?: boolean; allowedOrigins?: string[] }
): boolean {
	if (url == null || typeof url !== "string" || !url.trim()) return false;
	const u = url.trim();
	try {
		const parsed = new URL(u);
		const scheme = parsed.protocol;
		if (scheme !== "https:" && scheme !== "http:") return false;

		const origin = parsed.origin.toLowerCase();
		if (typeof window !== "undefined" && origin === window.location.origin) return true;

		const { allowLocalhost = true, allowedOrigins = DEFAULT_IFRAME_ALLOWLIST_ORIGINS } = options ?? {};
		if (allowLocalhost && (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1"))
			return true;

		return allowedOrigins.some((o) => origin === o.toLowerCase());
	} catch {
		return false;
	}
}
